import { Box } from '@mui/material';
import { AnalyticsSkuViewed } from '@repo/segment/Analytics/AnalyticsSkuViewed';
import { SEGMENT_SKU_VIEWED_SOURCE } from '@repo/segment/Analytics/constants';
import { MarketplaceEntityTypeEnum } from '@repo/supergraph/marketplace/enums';
import type { ProductCard as ProductCardType } from '@repo/supergraph/roundup/base/types';
import React, { useMemo } from 'react';
import { dynamicWithRetry } from '../../../DynamicWithRetry';
import { SECTION_NAME } from '../ProductCard/ImageCtaReviewSection/constants';
import { ProductCardProps } from '../ProductCard/types';

const ProductCard = dynamicWithRetry<ProductCardProps>(
  () => import('../ProductCard').then(mod => ({ default: mod.ProductCard })),
  { componentName: 'ProductCard' },
);

interface Props {
  cardBadgeText?: string;
  card: ProductCardType;
  cardIndex: number;
  productPositionBase: number;
  isSeoBankingFFActive: boolean;
  shouldApplyStylingUpdates?: boolean;
  isImproveQualityTrafficExperimentActive?: boolean;
  shouldDisplayWealthFrontModal?: boolean;
}

export default function ProductCardWrapper({
  cardBadgeText,
  card,
  cardIndex,
  productPositionBase,
  isSeoBankingFFActive,
  shouldApplyStylingUpdates = false,
  isImproveQualityTrafficExperimentActive = false,
  shouldDisplayWealthFrontModal = false,
}: Props): React.JSX.Element {
  const entity = card.marketplaceEntity;
  const elementPosition = cardIndex + productPositionBase;

  const overrideCta = useMemo(
    () => entity.ctaOverride || entity.cta,
    [entity.ctaOverride, entity.cta],
  );

  const { drivers, entityType, institution, name, offerForCta, product, productType } = entity;
  const monetizable = offerForCta?.monetizable ?? entity.monetizable!;
  const offerId = offerForCta?.id ? offerForCta.id : entity.id;
  const offerName = offerForCta?.name ? offerForCta.name : name;
  const ctaAnalytics = {
    index: elementPosition,
    ...(institution?.id && { institutionId: institution.id }),
    ...(entityType === MarketplaceEntityTypeEnum.Institutions && { institutionId: entity.id }),
    offerId,
    ...(product?.id && { productId: product.id }),
    productType,
    monetizable,
  };

  const marginTopStyles = cardIndex === 0 ? 0 : { xs: 5, md: 8 };

  return (
    <Box
      sx={{ marginTop: marginTopStyles, listStyle: 'none' }}
      key={`product-card-${elementPosition}`}
      component="li"
    >
      <AnalyticsSkuViewed
        source={SEGMENT_SKU_VIEWED_SOURCE}
        name={offerName}
        productType={productType}
        level="offer"
        institutionId={
          entityType === MarketplaceEntityTypeEnum.Institutions ? entity.id : entity.institution?.id
        }
        institutionName={
          entityType === MarketplaceEntityTypeEnum.Institutions ? name : entity.institution?.name
        }
        productId={entity.product?.id}
        productName={entity.product?.name}
        offerId={offerId}
        offerName={offerName}
        monetizable={monetizable}
        impressionSection={SECTION_NAME}
        impressionPosition={elementPosition}
        additionalAttributes={{
          page_type: 'roundup',
        }}
      >
        <div>
          <ProductCard
            ctaAnalytics={ctaAnalytics}
            bottomLine={card.bottomLine}
            cta={overrideCta}
            displayName={entity.displayName}
            drivers={drivers}
            federallyInsuredAnnotation={entity.federallyInsuredAnnotation}
            image={entity.image}
            monetizable={monetizable}
            starRating={entity.starRating}
            starRatingTooltip={entity.starRatingTooltip}
            reviewLink={entity.reviewLink}
            isSeoBankingFFActive={isSeoBankingFFActive}
            productType={entity.productType}
            subProductType={entity.subProductType}
            pros={card.pros}
            cons={card.cons}
            authorQuote={card.authorQuote}
            oneYearApyEntity={card.oneYearApyEntity}
            threeYearApyEntity={card.threeYearApyEntity}
            fiveYearApyEntity={card.fiveYearApyEntity}
            cardBadgeText={cardBadgeText}
            shouldApplyStylingUpdates={shouldApplyStylingUpdates}
            isImproveQualityTrafficExperimentActive={isImproveQualityTrafficExperimentActive}
            shouldDisplayWealthFrontModal={shouldDisplayWealthFrontModal}
            hideBestOfAwardsBanner={card.hideBestOfAwardsBanner}
            spotlightHeading={card.spotlightHeading}
            spotlightDetails={card.spotlightDetails}
          />
        </div>
      </AnalyticsSkuViewed>
    </Box>
  );
}
